import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { allWikiArticles } from '@/core/data/wikiArticlesIndex'
import {
  BookOpen, Search, Radar, Star, TrendingUp, ChevronRight,
  Settings, Shield, Zap, HelpCircle,
} from 'lucide-react'
import { cn } from '@/shared/lib/utils'

type Category = {
  id: string
  label: string
  icon: React.ElementType
  color: string
}

const categories: Category[] = [
  { id: 'getting-started', label: 'Démarrage', icon: Zap, color: '#C9A84C' },
  { id: 'llr', label: 'Local Lead Radar', icon: Radar, color: '#C9A84C' },
  { id: 'gfo', label: 'Google Footprint', icon: Search, color: '#7B61FF' },
  { id: 'are', label: 'Auto Reputation', icon: Star, color: '#E8562A' },
  { id: 'sdi', label: 'Search Demand Intel', icon: TrendingUp, color: '#0F7173' },
  { id: 'admin', label: 'Administration', icon: Shield, color: '#6B6050' },
]

export function TremorWikiPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const initialCat = searchParams.get('cat')
  const [searchQuery, setSearchQuery] = useState('')
  const [activeCategory, setActiveCategory] = useState<string | null>(initialCat)

  const filteredArticles = allWikiArticles.filter((a) => {
    const matchesSearch = !searchQuery ||
      a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.subtitle.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesCat = !activeCategory || a.categoryId === activeCategory
    return matchesSearch && matchesCat
  })

  // Group by category
  const groupedByCat = categories
    .map((cat) => ({
      ...cat,
      articles: filteredArticles.filter((a) => a.categoryId === cat.id),
    }))
    .filter((cat) => cat.articles.length > 0)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <p className="text-[9px] font-medium tracking-[0.22em] uppercase text-gold mb-1">
              Documentation · Wiki
            </p>
            <h1 className="font-serif text-[26px] font-bold text-ink tracking-[-0.03em] leading-tight">
              TremorWiki
            </h1>
            <p className="text-[11px] font-sans font-light text-ink-faint mt-1.5">
              {allWikiArticles.length} guides · Modes opératoires et paramétrage de la suite TremorApp
            </p>
          </div>
        </div>
        <div className="divider-gold mt-4" />
      </div>

      {/* Search */}
      <div className="relative max-w-lg">
        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-faint" />
        <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Rechercher un guide..."
          className="w-full rounded-lg border border-ink-border bg-white pl-10 pr-4 py-2.5 text-sm text-ink placeholder:text-ink-faint shadow-card focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold transition-all" />
      </div>

      {/* Category chips */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
        <button onClick={() => setActiveCategory(null)}
          className={cn('px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors',
            !activeCategory ? 'bg-onyx text-paper' : 'bg-paper-warm text-ink-muted border border-ink-border hover:border-gold')}>
          Tout ({allWikiArticles.length})
        </button>
        {categories.map((cat) => {
          const count = allWikiArticles.filter((a) => a.categoryId === cat.id).length
          if (count === 0) return null
          return (
            <button key={cat.id} onClick={() => setActiveCategory(activeCategory === cat.id ? null : cat.id)}
              className={cn('px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors',
                activeCategory === cat.id ? 'bg-onyx text-paper' : 'bg-paper-warm text-ink-muted border border-ink-border hover:border-gold')}>
              {cat.label} ({count})
            </button>
          )
        })}
      </div>

      {/* Articles grid grouped by category */}
      <div className="space-y-8">
        {groupedByCat.map((cat) => (
          <div key={cat.id}>
            <div className="flex items-center gap-2.5 mb-3">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${cat.color}15` }}>
                <cat.icon size={14} style={{ color: cat.color }} />
              </div>
              <h2 className="font-serif text-[15px] font-bold text-ink tracking-tight">{cat.label}</h2>
              <span className="text-[10px] text-ink-faint">{cat.articles.length} guide(s)</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {cat.articles.map((article) => (
                <button key={article.id}
                  onClick={() => navigate(`/wiki/${article.id}`)}
                  className="bg-white rounded-card border border-ink-border p-4 text-left shadow-card hover:shadow-elevated hover:-translate-y-px transition-all group relative overflow-hidden">
                  {/* Accent bar top */}
                  <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: cat.color }} />

                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <span className="text-[9px] text-ink-faint">Guide {article.guideNumber}/{article.totalGuides}</span>
                      <h3 className="font-sans text-[13px] font-semibold text-ink leading-snug mt-0.5">
                        {article.title}
                      </h3>
                    </div>
                    <ChevronRight size={13} className="text-ink-faint group-hover:text-gold transition-colors flex-shrink-0 mt-1" />
                  </div>
                  <p className="text-[11px] font-light text-ink-muted leading-relaxed">{article.subtitle}</p>
                  <div className="flex items-center gap-2 mt-3">
                    <span className="text-[9px] text-ink-faint">{article.readTime}</span>
                    <span className="text-[9px] text-ink-faint">· {article.sections.length} sections</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Empty state */}
      {filteredArticles.length === 0 && (
        <div className="text-center py-12">
          <HelpCircle size={40} className="mx-auto text-gold/30 mb-3" />
          <p className="font-serif text-[15px] text-ink">Aucun guide trouvé</p>
          <p className="text-[12px] font-light text-ink-muted mt-1">Essayez un autre terme de recherche.</p>
        </div>
      )}

      {/* Help footer */}
      <div className="bg-white rounded-card border border-ink-border p-5 shadow-card flex items-center gap-4">
        <div className="w-10 h-10 rounded-lg bg-gold-light flex items-center justify-center flex-shrink-0">
          <HelpCircle size={20} className="text-gold-dark" />
        </div>
        <div className="flex-1">
          <p className="text-[13px] font-semibold text-ink">Besoin d'aide ?</p>
          <p className="text-[11px] font-light text-ink-muted">Contactez votre administrateur ou écrivez-nous à support@tremorapp.net</p>
        </div>
      </div>
    </div>
  )
}
